import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { SideNav } from '@layouts/dashboard/side-nav'
import TopBar from '@layouts/dashboard/top-bar'
import { useState, createContext, useMemo, useEffect, useContext } from 'react'
import { IconButton, Box, useTheme, useMediaQuery, Alert } from '@mui/material'
import { ChevronLeftOutlined, ChevronRightOutlined } from '@mui/icons-material'
import { useSearchParams, useLocation } from 'react-router-dom'
import VerificationLink from '@layouts/verification-link'
import {
  BILLING,
  BILLING_QUERY_KEYS,
  INTERCOM,
  INTERCOM_QUERY_KEYS,
  PARTNER,
  PARTNER_QUERY_KEYS,
  USER,
  USER_QUERY_KEYS,
  WORKSPACE,
  WORKSPACE_QUERY_KEYS
} from '@services/api/api-constant'
import { useGetQuery } from '@services/api/network-request-service'
import { useDispatch, useSelector } from 'react-redux'
import { setUser } from '@services/user-service'
import { setAllWorkspacesData } from '@services/workSpace-service'
import { setPlan } from '@services/billing-service'
// import { AccountPageSkeleton } from '@components/skeleton/AccountPageSkeleton'
import { initializeTheme } from '@services/theme-service'
// import { s3ImageURL } from '@config'
import { useUser, useWorkspace } from '@hooks/user'
import { toggleNavMenu } from '@store/reducers/sideNavLayout'
import Loader from '@utils/loader'

const SIDE_NAV_WIDTH = 264
const TOP_NAV_HEIGHT = 64

const LayoutRoot = styled('div')(({ theme, user, isLoading }) => ({
  backgroundColor: 'rgba(0,0,0,0.01)',
  display: 'flex',
  flex: '1 1 auto',
  maxWidth: '100%',
  height: '100vh',
  [theme.breakpoints.up('lg')]: {
    paddingLeft: SIDE_NAV_WIDTH
  },
  [theme.breakpoints.down('lg')]: {
    marginTop: (user?.emailVerified || isLoading) ? TOP_NAV_HEIGHT : 0
  }
}))

const LayoutContainer = styled('div')({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  paddingTop: 16,
  // marginLeft: 16,
  paddingBottom: 0
  // overflowY: 'hidden'
})

// TODO: on logout do queryClient.clear();

export const SidebarContext = createContext()

export const Layout = ({ children }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('lg'))
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  // const [navMenuOpen, setNavMenuOpen] = useState(false)
  const navMenuOpen = useSelector((state) => state.sideNavLayout.navMenuOpen)
  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const iframe = searchParams.get('iframe')
  const reduxUser = useUser()
  const reduxWorkspace = useWorkspace()

  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError
  } = useGetQuery(USER_QUERY_KEYS.GET_ME, USER.GET_ME, {
    config: {
      staleTime: Infinity,
      cacheTime: Infinity
    }
  })

  const {
    data: customAttributesData,
    isLoading: isCustomAttributesLoading,
    refetch: refetchAttribute,
    isRefetching: attributeIsRefetching
  } = useGetQuery(
    INTERCOM_QUERY_KEYS.GET_CUSTOM_ATTRIBUTES,
    INTERCOM.GET_CUSTOM_ATTRIBUTES,
    {
      config: {
        enabled: Boolean(user && !user.isWhiteLabelUser),
        staleTime: Infinity,
        cacheTime: Infinity
      }
    }
  )

  const {
    data: workspaceData,
    isLoading: workspacesIsLoading,
    isError: isWorkspaceError
  } = useGetQuery(WORKSPACE_QUERY_KEYS.GET_ALL, WORKSPACE.GET_ALL)

  const workspaceId = workspaceData?.currentWorkspace?._id

  const { data: planData } = useGetQuery(
    BILLING_QUERY_KEYS.GET_PLAN(workspaceId),
    BILLING.GET_PLAN(workspaceId),
    { config: { enabled: !!workspaceId } }
  )

  const { data: partnerData } = useGetQuery(
    PARTNER_QUERY_KEYS.GET_PARTNER,
    PARTNER.GET_PARTNER,
    { config: { enabled: Boolean(user && (user.isPartner || user.partnerAccountId)) } }
  )

  const completionPercentage = useMemo(() => {
    if (user?.isWhiteLabelUser || !customAttributesData?.IntercomCustomAttributes) {
      return 80
    }
    const customAttributes = customAttributesData.IntercomCustomAttributes
    const conditions = [
      customAttributes?.email_account_linked === 'true',
      customAttributes?.contacts_added === 'true',
      customAttributes?.sequence_created === 'true',
      customAttributes?.sending_schedule_created === 'true',
      customAttributes?.campaign_reviewed === 'true',
      customAttributes?.campaign_launched === 'true',
      customAttributes?.team_members_invited === 'true',
      customAttributes?.lead_finder_search === 'true',
      customAttributes?.consultation_clicked === 'true'
    ]
    const counter = conditions.filter(Boolean).length
    return Math.floor((counter / conditions.length) * 100)
  }, [customAttributesData, user?.isWhiteLabelUser])

  const handleDrawerToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  useEffect(() => {
    if (user) {
      dispatch(setUser(user))
    }
  }, [user, dispatch])

  useEffect(() => {
    if (workspaceData?.currentWorkspace?._id) {
      dispatch(
        setAllWorkspacesData({
          uniqueWorkspaces: workspaceData?.uniqueWorkspaces || [],
          currentWorkspace: workspaceData?.currentWorkspace ?? null,
          workspacePlan: workspaceData?.currentPlan ?? null
        })
      )
    }
  }, [workspaceData, dispatch])

  useEffect(() => {
    if (planData) {
      dispatch(setPlan(planData?.plan))
    }
  }, [planData, dispatch])

  useEffect(() => {
    if (!partnerData) {
      return
    }

    const { theme, customDomain, seo, configs } = partnerData
    const updatedTheme = { ...theme }

    dispatch(
      initializeTheme({
        theme: updatedTheme,
        customDomain,
        seo,
        configs,
        isInitialized: true
      })
    )
  }, [partnerData, dispatch])

  const isLoading = isUserLoading || isCustomAttributesLoading || workspacesIsLoading
  const { isInitialized } = useSelector((state) => state.partnerDetails)
  const isPartner = user?.isPartner || user?.partnerAccountId

  if ((isLoading || !user || !workspaceData || !reduxWorkspace || !reduxUser) || (user && isPartner && isInitialized === false)) {
    return <Loader />
  }

  if (isUserError || isWorkspaceError) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <Alert severity='error'>
          Failed to load user data. Please try again.
        </Alert>
      </Box>
    )
  }

  const sidebarContextValue = {
    completionPercentage,
    onboardingData: customAttributesData,
    refresh: attributeIsRefetching,
    refetchAttribute
  }

  const layoutRootPaddingLeft = isMobile
    ? 0
    : navMenuOpen
      ? SIDE_NAV_WIDTH
      : `calc(${theme.spacing(8)} + 1px)`

  return (
    <SidebarContext.Provider value={sidebarContextValue}>
      {!iframe && !pathname.includes('workSpace') && (
        <>
          <TopBar
            handleDrawerToggle={handleDrawerToggle}
            user={user}
            isMobile={isMobile}
            isLoading={isLoading}
          />

          {!isMobile && (
            <>
              {user && !isLoading && !user?.emailVerified && (
                <Box
                  sx={{
                    backgroundColor: '#f9f9fa',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Alert
                    variant='outlined'
                    severity='warning'
                    sx={{
                      backgroundColor: '#fff',
                      width: '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: '14px'
                    }}
                  >
                    Your account needs verification to access all features.
                    <VerificationLink />
                  </Alert>
                </Box>
              )}
              <IconButton
                onClick={() => dispatch(toggleNavMenu())}
                sx={{
                  color: theme.palette.primary.contrastText,
                  backgroundColor: theme.palette.primary.main,
                  position: 'fixed',
                  left: navMenuOpen ? SIDE_NAV_WIDTH - 18 : `calc(${theme.spacing(8)} - 15px)`,
                  top: 45,
                  border: `2px solid ${theme.palette.primary.contrastText} `,
                  borderRadius: '50%',
                  padding: '2px',
                  zIndex: 1001,
                  transition: 'left 0.25s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main
                  }
                }}
              >
                {navMenuOpen
                  ? (
                    <ChevronLeftOutlined sx={{ fontSize: '28px' }} />
                    )
                  : (
                    <ChevronRightOutlined sx={{ fontSize: '28px' }} />
                    )}
              </IconButton>
            </>
          )}
          <SideNav
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            navMenuOpen={navMenuOpen}
          />
        </>
      )}

      <LayoutRoot
        user={user}
        isLoading={isLoading}
        style={{ paddingLeft: layoutRootPaddingLeft }}
      >
        <LayoutContainer>
          {children}
        </LayoutContainer>
      </LayoutRoot>
    </SidebarContext.Provider>
  )
}

export function useOnboarding () {
  return useContext(SidebarContext)
}

Layout.propTypes = {
  children: PropTypes.node
}
